<script>
import Quill from 'quill'
import defaultToolbar from './default-toolbar'
import mergeDeep from './merge-deep'
import MarkdownShortcuts from './markdown-shortcuts'
import OldApi from './old-api'
import DOMUtils from '@/utils/DOMUtils'
import StringUtils from '@/utils/stringUtils'
import Theme from '@/theme'

export default
{
    name: 'VueEditor',

    mixins: [OldApi],

    props:
    {
        placeholder: { type: String, default: '' },
        value: { type: String, default: '' },
        disabled: { type: Boolean },
        editorToolbar: { type: Array, default: () => [] },
        editorOptions: { type: Object, required: false, default: () => ({}) },
        useCustomImageHandler: { type: Boolean, default: false },
        useMarkdownShortcuts: { type: Boolean, default: false },
        showToolbar: { type: Boolean, default: true },
        maxLength: { type: Number, default: Infinity },
        noHtml: { type: Boolean, default: false },

        /** @values not-empty, email, phone */
        validation: { type: String, default: 'not-empty' },
        optional: { type: Boolean, default: false }
    },

    render ()
    {
        return (
            <div class='vue-editor'>
                <div class='quillWrapper'>
                    <slot name='toolbar' />
                    <div id='quill-container' ref='quillContainer' />
                    <input v-show={this.useCustomImageHandler} id='file-upload' ref='fileInput' type='file' accept='image/*'
                        style={{ display: 'none' }} on-change={this.emitImageInfo} />
                </div>
                <p class='hint-message' v-show={ !StringUtils.isEmptyString(this.hintMessage) }>{ this.hintMessage }</p>
            </div>
        )
    },

    data: () => (
    {
        quill: null,
        hintMessage: '',
        inputHtml: '',
    }),

    computed:
    {
        inputText ()
        {
            // delete the last \n added by quill getText()
            return this.quill.getText().replace(/\n([^/\n]*)$/, '$1')
        }
    },

    watch:
    {
        value (val, old)
        {
            if (val == old) return
            if (val != this.quill.root.innerHTML && !this.quill.hasFocus())
            {
                this.quill.root.innerHTML = val
            }

            // quill placeholder reactivity is broken when element is part of an array
            this.quill.root.dataset.placeholder = this.placeholder
        },

        inputHtml ()
        {
            this.$emit('input', this.inputHtml)
        },

        disabled (status)
        {
            this.quill.enable(!status)
        }
    },

    created ()
    {},

    mounted ()
    {
        this.registerCustomModules(Quill)
        this.registerPrototypes()
        this.initializeEditor()

        // focus & blur events, was made in this.handleSelectionChange but that was not effective enough for the blur event
        // which only was triggered when another form activated
        this.$refs.quillContainer.addEventListener('focus', this.onFocus, true)
        this.$refs.quillContainer.addEventListener('blur', this.onBlur, true)
        
        // if toolbar is shown the borders under must be flat
        if (this.showToolbar)
        {
            this.$refs.quillContainer.style.borderTopLeftRadius = 0
            this.$refs.quillContainer.style.borderTopRightRadius = 0
        }
    },

    beforeDestroy ()
    {
        this.quill = null
        delete this.quill
    },

    methods:
    {
        initializeEditor ()
        {
            this.setupQuillEditor()
            this.checkForCustomImageHandler()
            this.handleInitialContent()
            this.registerEditorEventListeners()
            this.$emit('ready', this.quill)
        },

        setupQuillEditor ()
        {
            let editorConfig =
            {
                debug: false,
                modules: this.setModules(),
                theme: 'snow',
                placeholder: this.placeholder ? this.placeholder : '',
                readOnly: this.disabled ? this.disabled : false
            }

            this.prepareEditorConfig(editorConfig)
            this.quill = new Quill(this.$refs.quillContainer, editorConfig)
        },

        setModules ()
        {
            let modules =
            {
                toolbar: this.editorToolbar.length ? this.editorToolbar : defaultToolbar
            }

            if (!this.showToolbar) modules.toolbar = false

            if (this.useMarkdownShortcuts)
            {
                Quill.register('modules/markdownShortcuts', MarkdownShortcuts, true)
                modules['markdownShortcuts'] = {}
            }

            return modules
        },

        prepareEditorConfig (editorConfig)
        {
            const hasEditorOptionsFields = Object.keys(this.editorOptions).length > 0
            const isEditorOptionsObject = this.editorOptions.constructor === Object
            
            if (hasEditorOptionsFields && isEditorOptionsObject)
            {
                if (this.editorOptions.modules && typeof this.editorOptions.modules.toolbar !== 'undefined')
                {
                    // We don't want to merge default toolbar with provided toolbar.
                    delete editorConfig.modules.toolbar
                }

                mergeDeep(editorConfig, this.editorOptions)
            }
        },

        registerPrototypes ()
        {
            Quill.prototype.getHTML = function ()
            {
                return this.container.querySelector('.ql-editor').innerHTML
            }

            Quill.prototype.getWordCount = function ()
            {
                return this.container.querySelector('.ql-editor').innerText.length
            }
        },

        registerEditorEventListeners ()
        {
            this.quill.on('text-change', this.handleTextChange)
            this.quill.on('selection-change', this.handleSelectionChange)
            this.listenForEditorEvent('text-change')
            this.listenForEditorEvent('selection-change')
            this.listenForEditorEvent('editor-change')
        },

        listenForEditorEvent (type)
        {
            this.quill.on(type, (...args) =>
            {
                this.$emit(type, ...args)
            })
        },

        handleInitialContent ()
        {
            if (this.value) this.quill.root.innerHTML = this.value // Set initial editor content
        },

        handleSelectionChange (range, oldRange)
        {
            //if (!range && oldRange) this.$emit('blur', this.quill),
            //else if (range && !oldRange) this.$emit('focus', this.quill)
        },

        handleTextChange (delta, oldContents)
        {
            if (this.maxLength != Infinity) this.limitTextLength(this.maxLength, delta)

            let editorContent = this.quill.getHTML() === '<p><br></p>' ? '' : this.quill.getHTML()

            if (this.noHtml)
            {
                editorContent = this.inputText
            }

            this.inputHtml = editorContent
            this.$emit('input', editorContent)

            if (this.useCustomImageHandler)
            {
                this.handleImageRemoved(delta, oldContents)
            }
        },

        handleImageRemoved(delta, oldContents)
        {
            const currrentContents = this.quill.getContents()
            const deletedContents = currrentContents.diff(oldContents)
            const operations = deletedContents.ops

            operations.map(operation =>
            {
                if (operation.insert && operation.insert.hasOwnProperty('image'))
                {
                    const { image } = operation.insert
                    this.$emit('image-removed', image)
                }
            })
        },

        checkForCustomImageHandler ()
        {
            this.useCustomImageHandler === true ? this.setupCustomImageHandler() : ''
        },

        setupCustomImageHandler ()
        {
            let toolbar = this.quill.getModule('toolbar')
            toolbar.addHandler('image', this.customImageHandler)
        },

        customImageHandler (image, callback)
        {
            this.$refs.fileInput.click()
        },

        emitImageInfo ($event)
        {
            const resetUploader = function ()
            {
                let uploader = document.getElementById('file-upload')
                uploader.value = ''
            }

            let file = $event.target.files[0]
            let Editor = this.quill
            let range = Editor.getSelection()
            let cursorLocation = range.index

            this.$emit('image-added', file, Editor, cursorLocation, resetUploader)
        },

        limitTextLength (limit, delta)
        {
            const exceededSize = this.quill.getText().replace('\n', '').length - limit

            if (exceededSize <= 0)
            {
                return
            }

            const { ops } = delta
            let updatedOps

            if (ops.length === 1)
            {
                // text is inserted at the beginning
                updatedOps = [{ delete: exceededSize }] 
            }
            else
            {
                // ops[0] == {retain: numberOfCharsBeforeInsertedText}
                // ops[1] == {insert: "example"}
                updatedOps = [ops[0], { delete: exceededSize }] 
            }   

            this.quill.updateContents({ ops: updatedOps }) 
        },

        onFocus (event)
        {
            this.$emit('focus', this.quill)

            this.setDefaultStyle()
            this.hintMessage = undefined

            if (!this.showToolbar)
            {
                this.setBorderColor(Theme.properties.accentColor)
            }
        },

        onBlur (event)
        {
            this.$emit('blur', this.quill)

            if (!this.showToolbar)
            {
                this.setBorderColor(Theme.properties.borderColor)
            }

        },

        setBorderColor (color)
        {
            this.$refs.quillContainer.style.borderColor = color
        },

        setDefaultStyle ()
        {
            this.$el.classList.remove('error')
            this.setBorderColor(Theme.properties.borderColor)
        },

        setErrorStyle ()
        {
            this.$el.classList.add('error')
            this.setBorderColor('red')
        },

        setErrorMessage (message)
        {
            this.hintMessage = message
            this.setErrorStyle()
        }
    }
}
</script>

<style src='quill/dist/quill.snow.css'></style>
<style src='./vue2-editor.less' lang='less'></style>
<style lang='less'>
@import '~@/styles/main.less';

.vue-editor
{
    color: var(--theme-font-color-extra-light);
    font-size: 4vw;

    .OnTablet({
        font-size: 2.2vw;
    });

    .OnDesktop({
        font-size: 1.7vh;
    });

    .hint-message
    {
        font-size: 2vh;
        margin-top: 5px;
        margin-left: 5px;

        .OnLargeMobile({ font-size: 1.7vh; });
        .OnTablet({ font-size: 1.5vh; });
        .OnDesktop({ font-size: inherit; });
    }

    &.error
    {
        .ql-toolbar.ql-snow
        {
            border-bottom-color: red;
        }

        .hint-message
        {
            color: red;
        }
    }
}
</style>


// WEBPACK FOOTER //
// src/components/vue2-editor/VueEditor.vue